// One document, laid out the way it will be keyed into QuickBooks.
//
// This is the screen the last three migrations were for. The billing home says
// what is coming and what is stuck; this is where a bill run stops being a
// query and becomes a piece of paper: a payer, a date, ordered lines, a total,
// and — the part that makes pre-filling worth doing — lines that are allowed to
// exist before anybody knows what they cost.
//
// TWO KINDS OF LINE, and the difference is visible rather than explained. A
// DERIVED line names a term line, so its figures are that clause's statement
// for the period, frozen the moment the document went out. A STANDALONE line
// carries its own words — a credit, a discount, or a charge whose artefact does
// not exist yet, which is the Kingsport case: the July expenses are certain and
// the base is with Hansen.
//
// A DRAFT IS EDITABLE AND NOTHING ELSE IS. That is enforced in the database
// (migrations 0018/0019), not here; what this screen does is stop offering the
// controls, because a button that always 422s is worse than no button.
//
// WHO IT IS FOR, AND WHO PAYS — and why this screen alone keeps the payer
// first. Everywhere else in billing the customer leads, because a list of
// documents is read by whose service each one bills and five rows saying
// "Hansen CIS B" are five rows a person cannot tell apart. A document is not a
// list. This one is addressed TO the partner, its number is the number Hansen
// will quote back, and the header block is the part somebody keys into
// QuickBooks — so demoting the payer there would be demoting the fact the block
// exists to state. The customers are named beside it instead, and everything
// that is not the address — the page's own subtitle, each line's sub-line —
// leads with them.

import { useCallback, useState, type ReactNode } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  InvoiceLineCategory,
  isIssued,
  type Currency,
  type BillingContact,
  type InvoiceLineListItem,
  type InvoiceListItem,
} from "@crm/shared";
import styles from "./InvoiceSheet.module.css";
import { CUSTOMERS_IN_A_CELL, describeParties, summariseCustomers } from "./customers.js";
import { InvoiceLineDialog } from "./InvoiceLineDialog.js";
import { IssueInvoiceDialog } from "./IssueInvoiceDialog.js";
import { InvoiceApprovalDialog } from "./InvoiceApprovalDialog.js";
import { Badge, type Tone } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card, CardHeader } from "../../design/Card.js";
import { Notice } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th } from "../../design/Table.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { useBillingContacts, useInvoice, useInvoiceLines } from "../../api/queries.js";
import { useInvoiceLineMutations } from "../../api/mutations.js";
import { formatCalendarDate, formatMonth } from "../../format/calendarDate.js";
import { formatMoney, formatPercent, formatQuantity, formatUnitPrice } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";
import { revShareDescription } from "../../format/revShare.js";
import { chargeTypeLabel } from "../../format/labels.js";
import { ChargeMixBadge } from "../../components/ChargeMix.js";
import { useHasRole } from "../../auth/useAuth.js";
import { PendingAmount } from "../../components/SplitSource.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";

const STATUS_TONE: Record<InvoiceListItem["status"], Tone> = {
  draft: "neutral",
  issued: "info",
  paid: "good",
  void: "bad",
};

export function InvoiceSheetPage(): ReactNode {
  const { invoiceId } = useParams({ from: "/invoices/$invoiceId" });
  const invoice = useInvoice(invoiceId);
  const lines = useInvoiceLines(invoiceId);

  // The title names the customers where the shell has room for them, because
  // the shell's title is what a tab and a back-navigation show — "Invoice" told
  // a reader with three of these open nothing at all.
  usePageChrome({
    title: invoice.data === undefined ? "Invoice" : titleFor(invoice.data),
  });

  return (
    <div className={styles.page}>
      <QueryBoundary query={invoice} label="this invoice">
        {(document) => (
          <QueryBoundary query={lines} label="its lines">
            {(envelope) => <Sheet invoice={document} lines={envelope.items} />}
          </QueryBoundary>
        )}
      </QueryBoundary>
    </div>
  );
}

/**
 * The shell's title: "Invoice · Kingsport, Bristol".
 *
 * Two names at most, since a title bar truncates and the count is what carries
 * the rest. A document that names no customer keeps the bare word, because
 * "Invoice · partner-wide" in a tab reads as a category and not as this one.
 */
export function titleFor(invoice: InvoiceListItem): string {
  const summary = summariseCustomers(invoice.customers, CUSTOMERS_IN_A_CELL);
  return summary.isPartnerWide ? "Invoice" : `Invoice · ${summary.label}`;
}

/**
 * The subtitle: who it is for, who pays, when, and what state.
 *
 * `describeParties` puts the customers first here — this line is how somebody
 * recognises WHICH invoice they opened, and the partner alone never answered
 * that. The header block below still leads with the payer; see the note at the
 * top of this file for why the two orders are deliberate rather than a drift.
 */
function describeInvoice(invoice: InvoiceListItem): string {
  const period =
    invoice.service_period === null ? "" : ` · covers ${formatMonth(invoice.service_period)}`;
  const number = invoice.invoice_number === null ? "Draft" : invoice.invoice_number;
  return `${number} · ${describeParties(invoice)} · due ${formatCalendarDate(invoice.due_date)}${period}`;
}

function Sheet({
  invoice,
  lines,
}: {
  invoice: InvoiceListItem;
  lines: readonly InvoiceLineListItem[];
}): ReactNode {
  const isEditor = useHasRole("crm_editor");
  const canEdit = isEditor && !isIssued(invoice.status);
  const [recordingApproval, setRecordingApproval] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<InvoiceLineListItem | null>(null);
  const [deleting, setDeleting] = useState<InvoiceLineListItem | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [copied, setCopied] = useState(false);
  const mutations = useInvoiceLineMutations();

  // Not memoised: it walks the lines once, so a memo would only be somewhere
  // for the button and the notice to disagree.
  const blockers = issueBlockers(invoice, lines);
  const pendingLines = invoice.pending_line_count;

  const copyForQuickBooks = useCallback(() => {
    void navigator.clipboard.writeText(quickBooksText(invoice, lines)).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    });
  }, [invoice, lines]);

  return (
    <>
      <div className={styles.head}>
        <div>
          <div className={styles.title}>
            {invoice.invoice_number ?? "Draft invoice"}
            <Badge tone={STATUS_TONE[invoice.status]} shape="chip">
              {invoice.status}
            </Badge>
            {/* WHICH HALF OF THE SYSTEM THIS DOCUMENT'S MONEY IS, beside its
                status, because the two together are what a reader needs before
                reading a single figure below. The lines each say it too — but a
                reader who has to add up eight source badges to learn that this
                is an expense recovery has been told nothing at the point they
                needed it. */}
            <ChargeMixBadge
              shape="chip"
              counts={{
                bills: invoice.bill_line_count,
                recharges: invoice.recharge_line_count,
                standalone: invoice.standalone_line_count,
              }}
            />
          </div>
          {/* The ADDRESS. The payer leads it and keeps the link, because this
              is the block that says where the document goes and what it is
              denominated in — the facts that get keyed into QuickBooks. */}
          <div className={styles.subtitle}>
            <Link to="/partners/$partnerId" params={{ partnerId: invoice.partner_id }}>
              {invoice.partner_name}
            </Link>
            {` · ${invoice.currency} · due ${formatCalendarDate(invoice.due_date)}`}
            {invoice.issue_date !== null && ` · issued ${formatCalendarDate(invoice.issue_date)}`}
          </div>
          {/* And beneath it, WHOSE service this bills — its own line rather
              than a fourth clause on the address, and set at full strength
              rather than whispered, because it is what tells one Hansen invoice
              from the next. An invoice whose lines point at no deal says that
              out loud too; a blank line here would read as a load that failed
              rather than as the answer it is. */}
          <div className={styles.forWhom}>
            {invoice.customers.length === 0
              ? "Partner-wide · nothing on it points at a deal"
              : /* Every name, uncounted: the list truncates a ROW because the
                   next row needs the space, and this page has no next row. */
                `For ${invoice.customers.join(", ")}`}
          </div>
          {/* Where it stands with its approver. Said even when nothing is
              recorded, because "not sent" is the state somebody has to act on. */}
          <div className={styles.approval}>{approvalSentence(invoice)}</div>
          <SendTo invoice={invoice} lines={lines} />
        </div>
        <div className={styles.actions}>
          {canEdit && (
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Add a line
            </Button>
          )}
          {/* Offered on an issued invoice too: an approval is a fact about the
              document, not an amendment to it. */}
          {isEditor && (
            <Button variant="secondary" onClick={() => setRecordingApproval(true)}>
              Approval
            </Button>
          )}
          <Button variant="secondary" onClick={copyForQuickBooks} disabled={lines.length === 0}>
            {copied ? "Copied" : "Copy for QuickBooks"}
          </Button>
          {canEdit && (
            <Button
              variant="primary"
              onClick={() => setIssuing(true)}
              disabled={blockers.length > 0}
              title={blockers[0]}
            >
              Issue
            </Button>
          )}
        </div>
      </div>

      {/* Said once, at the top, rather than repeated per row: the reader wants
          to know whether they can send this thing, and the answer is one
          sentence about the whole document. */}
      {blockers.length > 0 && !isIssued(invoice.status) && (
        <Notice tone="warn" title="This cannot go out yet.">
          {blockers.join(" ")}
        </Notice>
      )}

      {isIssued(invoice.status) && (
        <Notice tone="info" title={`This invoice was ${invoice.status}.`}>
          {`It says what it said — its lines and its header are fixed. Correct it with a credit ` +
            `note: a new invoice pointing back at this one, with negative lines.`}
        </Notice>
      )}

      <Card>
        <CardHeader
          title="Lines"
          description={
            canEdit
              ? "In the order they appear on the invoice. A line may exist before its amount does."
              : "In the order they appear on the invoice."
          }
          actions={
            pendingLines > 0 ? (
              <Badge tone="warn" shape="chip">
                {`${String(pendingLines)} pending`}
              </Badge>
            ) : undefined
          }
        />
        <Table label={`Lines on invoice ${invoice.invoice_number ?? "draft"}`}>
          <thead>
            <tr>
              <Th>#</Th>
              <Th>Description</Th>
              <Th>Covers</Th>
              <Th>Source</Th>
              {/* THE FIGURES IN THE ORDER THE ARITHMETIC HAPPENS: how many
                  units, what one costs, what the end customer was therefore
                  charged, the rate we keep out of it, and what this document
                  asks the partner for. "Amount" is the net and on its own says
                  nothing about which customer or which rate produced it — the
                  four columns in front of it are what make it checkable without
                  re-deriving anything. */}
              <Th numeric>Quantity</Th>
              <Th numeric>Unit price</Th>
              <Th numeric>Customer billed</Th>
              <Th numeric>Share</Th>
              <Th numeric>Amount</Th>
              <Th numeric>Ours</Th>
              {canEdit && <Th />}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <EmptyRow
                colSpan={canEdit ? 11 : 10}
                title="Nothing on this invoice yet"
                description="Add the charges it covers, and any line that has no term line behind it — including one you cannot price yet."
              />
            ) : (
              lines.map((line) => (
                <LineRow
                  key={line.id}
                  line={line}
                  currency={invoice.currency}
                  canEdit={canEdit}
                  onEdit={() => setEditing(line)}
                  onDelete={() => setDeleting(line)}
                />
              ))
            )}
          </tbody>
          {lines.length > 0 && (
            <tfoot>
              <tr>
                <Td colSpan={6}>
                  {`Total · ${String(invoice.line_count)} ${invoice.line_count === 1 ? "line" : "lines"}`}
                </Td>
                <Td numeric mono muted>
                  {formatMoney(invoice.gross_minor, invoice.currency, {
                    omitCurrencyCode: true,
                  })}
                </Td>
                {/* The document's BLENDED rate — our share over the base, not an
                    average of the line rates. Null on a document with no revenue
                    on it (a pure cost recovery), where a percentage would be
                    describing a cost split as though it were a rev share. */}
                <Td numeric mono muted>
                  {invoice.effective_split_pct === null
                    ? "—"
                    : formatPercent(invoice.effective_split_pct)}
                </Td>
                <Td numeric mono>
                  {formatMoney(invoice.charged_minor, invoice.currency, {
                    omitCurrencyCode: true,
                  })}
                </Td>
                <Td numeric mono>
                  {formatMoney(invoice.our_share_minor, invoice.currency, {
                    omitCurrencyCode: true,
                  })}
                </Td>
                {canEdit && <Td />}
              </tr>
            </tfoot>
          )}
        </Table>
        <div className={styles.footnote}>
          {`${invoice.currency} · pre-tax — add HST in QuickBooks. A quantity times a unit price will not always print out to the amount beside it: a unit price is exact to nine places, and the amount was rounded once, after the multiplication. `}
          {pendingLines > 0
            ? `The total covers ${String(invoice.line_count - invoice.pending_line_count)} of ${String(invoice.line_count)} lines; the rest have no figure yet.`
            : isIssued(invoice.status)
              ? "Every line is a snapshot. Nothing on this document can move again."
              : "Every line is priced. A draft re-reads its figures, so this total moves until it is issued."}
        </div>
        {/* THE TOTAL, SPLIT, on the one document where the total on its own
            describes nothing: a document carrying both halves. On a document
            that is all one half the badge in the header has already said which,
            and repeating the whole figure under a different name would be a
            second total to reconcile against the first. */}
        {invoice.bill_line_count > 0 && invoice.recharge_line_count > 0 && (
          <div className={styles.footnote}>
            {`Of that total, `}
            <span className="mono">
              {formatMoney(invoice.bill_charged_minor, invoice.currency, {
                omitCurrencyCode: true,
              })}
            </span>
            {` is charged for what we sell and `}
            <span className="mono">
              {formatMoney(invoice.recharge_charged_minor, invoice.currency, {
                omitCurrencyCode: true,
              })}
            </span>
            {` recovers costs of ours`}
            {invoice.standalone_line_count > 0 && (
              <>
                {`, with `}
                <span className="mono">
                  {formatMoney(invoice.standalone_charged_minor, invoice.currency, {
                    omitCurrencyCode: true,
                  })}
                </span>
                {` correcting the document`}
              </>
            )}
            {`.`}
          </div>
        )}
      </Card>

      {adding && <InvoiceLineDialog invoice={invoice} onClose={() => setAdding(false)} />}
      {editing !== null && (
        <InvoiceLineDialog invoice={invoice} line={editing} onClose={() => setEditing(null)} />
      )}
      {issuing && <IssueInvoiceDialog invoice={invoice} onClose={() => setIssuing(false)} />}
      {recordingApproval && (
        <InvoiceApprovalDialog invoice={invoice} onClose={() => setRecordingApproval(false)} />
      )}
      {deleting !== null && (
        <DeleteDialog
          open
          title="Take this line off the invoice?"
          description={`"${deleting.resolved_description}" comes off this draft. The term line it points at is untouched — the period stays open, unbilled.`}
          record={deleting}
          remove={(id, version) => mutations.remove.mutateAsync({ id, version })}
          fetchCurrent={mutations.fetchCurrent}
          onClose={() => setDeleting(null)}
          onDeleted={() => setDeleting(null)}
        />
      )}
    </>
  );
}

function LineRow({
  line,
  currency,
  canEdit,
  onEdit,
  onDelete,
}: {
  line: InvoiceLineListItem;
  currency: Currency;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): ReactNode {
  return (
    <tr>
      <Td mono muted>
        {line.sort_order}
      </Td>
      <Td strong>
        {line.resolved_description}
        {/* Whose service this line bills, at the same weight as the line's own
            words. It stays UNDER the description rather than above it — the
            description is what the customer reads on the paper, and reordering
            them would make the screen stop matching the document — but it is no
            longer a whisper: on a run covering four customers, "Grace AI
            subscription" four times over is only distinguishable by this. */}
        {line.customer_name !== null && (
          <div className={styles.lineCustomer}>{line.customer_name}</div>
        )}
        {line.notes !== null && <div className={styles.note}>{line.notes}</div>}
        {/* HOW THE FIGURE WAS REACHED, on the document that carries it. An
            issued line is a snapshot of a statement line, and `breakdown` is
            the evidence that produced it — the fact counts, the rates applied,
            the prepaid allocation, the share resolution. Behind a disclosure
            because a reader checking a total does not want it open, and on the
            page because the alternative is re-deriving a figure that already
            went out. */}
        {line.breakdown !== null && <Breakdown breakdown={line.breakdown} />}
      </Td>
      <Td muted mono nowrap>
        {line.service_period === null ? "—" : formatMonth(line.service_period)}
      </Td>
      {/* WHAT THIS LINE IS FOR, then what kind of row it came from.
          "Bill" is true of most of a document and so distinguishes none of it —
          an eight-line run said it eight times and never said which of them was
          the subscription, which the implementation milestone and which the
          telephony recovery. The charge type is the half that answers that, so
          it leads and carries the colour; the source kind stays because it is
          still a different fact — a term-line row is derived and a standalone
          one was typed — but it drops to the quietest tone there is, as the
          context it now is. */}
      <Td>
        <div className={styles.source}>
          {line.charge_type !== null && (
            <Badge shape="tag" tone="slate">
              {chargeTypeLabel(line.charge_type)}
            </Badge>
          )}
          <Badge shape="tag" tone={sourceTone(line)}>
            {sourceLabel(line)}
          </Badge>
        </div>
      </Td>
      {/* THE ARITHMETIC, SPELLED OUT. A quantity, a unit and a unit price are
          what make the amount beside them checkable without opening anything:
          "412 minutes at 0.80" either does or does not come to what the line
          says. A dash where a line has none — a flat charge counts the period
          itself and has no unit price to state.

          The pair will not always multiply out to the gross, and the footnote
          under the table says so: a unit price is exact to nine places and the
          gross was rounded once, after the multiplication. */}
      <Td numeric mono muted nowrap>
        {line.quantity === null
          ? "—"
          : `${formatQuantity(line.quantity)}${line.unit === null ? "" : ` ${line.unit}`}`}
        {/* What this line left of the prepaid block it drew on, frozen at
            issue. It is what a later cycle's carry-in reads, so it belongs on
            the document rather than only in the evaluator. */}
        {line.prepaid_remaining !== null && (
          <div className={styles.note}>{`${line.prepaid_remaining} left in the block`}</div>
        )}
      </Td>
      <Td numeric mono muted>
        {line.unit_price_minor === null
          ? "—"
          : formatUnitPrice(line.unit_price_minor, currency, { omitCurrencyCode: true })}
      </Td>
      {/* What the end customer was charged. A dash rather than a zero on a
          standalone line, which was not a share of anything — the line IS its
          own amount, and a 0.00 here would read as "the customer paid nothing". */}
      <Td numeric mono muted>
        {line.gross_minor === null ? (
          <span className={styles.missing}>—</span>
        ) : (
          formatMoney(line.gross_minor, currency, { omitCurrencyCode: true })
        )}
      </Td>
      <Td numeric mono muted>
        {line.applied_split_pct === null ? "—" : formatPercent(line.applied_split_pct)}
      </Td>
      <Td numeric mono strong>
        {line.charged_minor === null ? (
          <PendingAmount />
        ) : (
          formatMoney(line.charged_minor, currency, { omitCurrencyCode: true })
        )}
      </Td>
      <Td numeric mono muted>
        {line.our_share_minor === null ? (
          <span className={styles.missing}>—</span>
        ) : (
          formatMoney(line.our_share_minor, currency, { omitCurrencyCode: true })
        )}
      </Td>
      {canEdit && (
        <Td>
          <div className={styles.rowActions}>
            {/* Named for the LINE, not just "Edit": the rows on a document
                are near-identical by design, so a screen reader hearing eight
                "Edit" buttons has been told nothing. */}
            <Button
              small
              variant="subtle"
              onClick={onEdit}
              aria-label={`Edit ${line.resolved_description}`}
            >
              Edit
            </Button>
            <Button
              small
              variant="subtle"
              onClick={onDelete}
              aria-label={`Remove ${line.resolved_description}`}
            >
              Remove
            </Button>
          </div>
        </Td>
      )}
    </tr>
  );
}

/**
 * The evidence behind one frozen figure.
 *
 * Rendered as the JSON the server wrote, formatted, and not as a table of
 * named fields: the breakdown's shape follows the term line that produced it —
 * a fact count for one, a prepaid allocation for another — so a fixed table
 * would either hide half of it or invent labels the server never sent. A
 * `<details>` keeps it out of the way of a reader who is checking a total.
 */
function Breakdown({ breakdown }: { breakdown: Record<string, unknown> }): ReactNode {
  return (
    <details className={styles.breakdown}>
      <summary>How this figure was reached</summary>
      <pre className={styles.breakdownBody}>{JSON.stringify(breakdown, null, 2)}</pre>
    </details>
  );
}

/**
 * What a line points at, in one word — the second half of the source cell on a
 * linked line, and the whole of it on a standalone one.
 *
 * A category when it has one, since "Credit" says more about a standalone line
 * than "Standalone" does. That branch is also why a standalone line grows no
 * charge type beside it: `charge_type` is resolved from the schedule item's
 * revenue line or the recharge's shared cost, and a line with no source has
 * neither — its category IS what it would have said.
 */
function sourceLabel(line: InvoiceLineListItem): string {
  if (line.category !== null) return categoryLabel(line.category);
  switch (line.source_kind) {
    case "term_line":
      return "Derived";
    case "standalone":
      return "Manual";
    default:
      return assertNever(line.source_kind, "invoice line source kind");
  }
}

function categoryLabel(category: InvoiceLineCategory): string {
  return category.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/**
 * How loudly the source badge is drawn — which depends on whether it is the
 * cell's only word.
 *
 * A standalone line has always been the quiet one: it points at nothing, so
 * there is nothing to go and look at, and its own words already say what it is.
 * A linked line kept the louder tone for the opposite reason — it names a row
 * elsewhere. That stops being true once the charge type sits in front of it and
 * takes the reading; two tags at equal weight in one cell is two things
 * competing to be read first, so the source steps back to the quiet tone.
 */
function sourceTone(line: InvoiceLineListItem): Tone {
  if (line.charge_type !== null) return "neutral";
  return line.source_kind === "standalone" ? "neutral" : "slate";
}

/**
 * Everything standing between this draft and going out, in the order somebody
 * would fix them.
 *
 * The same rules `crm_issue_invoice` enforces, said before the button is pressed
 * rather than after. Not a second implementation of them in any meaningful
 * sense — the database is still what refuses — but a disabled button with a
 * reason beats an enabled one that 422s.
 */
export function issueBlockers(
  invoice: InvoiceListItem,
  lines: readonly InvoiceLineListItem[],
): string[] {
  if (isIssued(invoice.status)) return [];
  const blockers: string[] = [];
  if (lines.length === 0) {
    blockers.push("There is nothing on it.");
  }
  const pending = invoice.pending_line_count;
  if (pending > 0) {
    blockers.push(
      `${String(pending)} line${pending === 1 ? "" : "s"} ` +
        `still ${pending === 1 ? "has" : "have"} no amount — an invoice with a ` +
        `blank on it is not an invoice.`,
    );
  }
  return blockers;
}

/**
 * The document as plain text, for pasting into QuickBooks.
 *
 * Tab-separated, because that is what a spreadsheet and QuickBooks' own line
 * grid both paste into as columns. Amounts as plain decimals with no symbol and
 * no thousands separator: the destination formats them, and a `$` or a comma is
 * exactly what makes a paste land as text instead of as a number.
 *
 * Exported for tests — this is the one output on this screen that is consumed by
 * something outside the app, so its shape is a contract.
 */
export function quickBooksText(
  invoice: InvoiceListItem,
  lines: readonly InvoiceLineListItem[],
): string {
  const header = [
    `Invoice to ${invoice.partner_name}`,
    `Due ${invoice.due_date}`,
    `${invoice.currency}, pre-tax`,
  ].join("\t");

  // THE DESCRIPTION CARRIES THE ARITHMETIC, which is the whole reason this
  // export is worth having: the amount column holds the net, and
  // "Grace AI SaaS Subscription Fee ($1,750 @ 70% Rev Share)" is what tells the
  // person reading the finished invoice — and the partner receiving it — what
  // that net is a share of. Composed rather than retyped, so the wording on the
  // paper cannot drift from the figures this system holds.
  const body = lines.map((line) =>
    [
      revShareDescription(line, invoice.currency),
      line.service_period ?? "",
      line.charged_minor === null ? "" : (line.charged_minor / 100).toFixed(2),
    ].join("\t"),
  );

  const total = ["TOTAL", "", (invoice.charged_minor / 100).toFixed(2)].join("\t");
  return [header, "", ...body, total].join("\n");
}

/** Who the invoice is emailed to, as a person reads and copies it. */
interface Recipient {
  name: string | null;
  email: string;
}

/**
 * WHO THIS GOES TO, under the address: the people the payer named to receive
 * invoices, so nobody hunts for an email address after keying the document.
 *
 * The deal's own recipients when the document is for one deal and that deal
 * names any, else the partner's — the server's rule for
 * `effective_billing_contacts`, read for the one role this line is about. The
 * CRM sends nothing: this is what goes in QuickBooks' "To" field.
 */
function SendTo({
  invoice,
  lines,
}: {
  invoice: InvoiceListItem;
  lines: readonly InvoiceLineListItem[];
}): ReactNode {
  const dealIds = new Set(lines.flatMap((line) => (line.deal_id === null ? [] : [line.deal_id])));
  const [only] = dealIds;
  const dealId = dealIds.size === 1 && only !== undefined ? only : null;
  return <Recipients partnerId={invoice.partner_id} dealId={dealId} />;
}

function Recipients({
  partnerId,
  dealId,
}: {
  partnerId: string;
  dealId: string | null;
}): ReactNode {
  const partner = useBillingContacts({ kind: "partner", id: partnerId });
  // A document spanning deals has no one deal to ask, so it asks the partner
  // twice rather than conditionally calling a hook.
  const deal = useBillingContacts(
    dealId === null ? { kind: "partner", id: partnerId } : { kind: "deal", id: dealId },
  );
  if (partner.data === undefined || deal.data === undefined) return null;
  const recipientsOf = (items: readonly BillingContact[]): BillingContact[] =>
    items.filter((contact) => contact.role === "recipient");
  const own = dealId === null ? [] : recipientsOf(deal.data.items);
  return <RecipientLine recipients={own.length > 0 ? own : recipientsOf(partner.data.items)} />;
}

function RecipientLine({ recipients }: { recipients: readonly Recipient[] }): ReactNode {
  const [copied, setCopied] = useState(false);
  if (recipients.length === 0) {
    return (
      <div className={styles.approval}>
        Send to: nobody on file — add a billing contact on the partner or the deal.
      </div>
    );
  }
  const emails = recipients.map((recipient) => recipient.email).join(", ");
  const copy = (): void => {
    void navigator.clipboard.writeText(emails).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    });
  };
  return (
    <div className={styles.sendTo}>
      {`Send to ${recipients
        .map((recipient) =>
          recipient.name === null ? recipient.email : `${recipient.name} <${recipient.email}>`,
        )
        .join(", ")}`}
      <button type="button" className={styles.copyInline} onClick={copy}>
        {copied ? "Copied" : recipients.length === 1 ? "Copy email" : "Copy emails"}
      </button>
    </div>
  );
}

/** Where the invoice stands with its approver, in one sentence. */
function approvalSentence(invoice: InvoiceListItem): string {
  if (invoice.approved_on !== null) {
    const by = invoice.approved_by === null ? "" : ` by ${invoice.approved_by}`;
    return `Approved ${formatCalendarDate(invoice.approved_on)}${by}`;
  }
  if (invoice.sent_for_approval_on !== null) {
    const to = invoice.sent_for_approval_to === null ? "" : ` to ${invoice.sent_for_approval_to}`;
    return `Sent for approval ${formatCalendarDate(invoice.sent_for_approval_on)}${to} · not approved yet`;
  }
  return "Not sent for approval";
}
